/**
 * 预览播放时钟：用 rAF 帧时间戳积分，避免 `performance.now()` 与首个回调 `ts` 的时间差
 * 在 vue watch 异步 flush / 标签页恢复后被算入「第一帧 dt」，导致一次性跳到末尾。
 *
 * - 首次 step 的 `dt` 强制为 0（仅用首帧 ts 初始化 `lastTs`）。
 * - 单帧 dt 上限 `MAX_DT_SEC` 防止 GC / tab 切换造成异常跳跃。
 */
const MAX_DT_SEC = 0.1

export class PlaybackController {
  private rafId = 0
  private lastTs = 0
  private hasFirstFrame = false
  private readonly getIsPlaying: () => boolean
  private readonly stopPlaying: () => void
  private readonly getDurationSec: () => number
  private readonly getCurrentSec: () => number
  private readonly setCurrentSec: (sec: number) => void

  /**
   * @param getIsPlaying - 是否仍在播放
   * @param stopPlaying - 停止播放（到达末尾时调用）
   * @param getDurationSec - 时间轴总时长（秒）
   * @param getCurrentSec - 当前时间（秒）
   * @param setCurrentSec - 写入当前时间（秒）
   */
  constructor(
    getIsPlaying: () => boolean,
    stopPlaying: () => void,
    getDurationSec: () => number,
    getCurrentSec: () => number,
    setCurrentSec: (sec: number) => void,
  ) {
    this.getIsPlaying = getIsPlaying
    this.stopPlaying = stopPlaying
    this.getDurationSec = getDurationSec
    this.getCurrentSec = getCurrentSec
    this.setCurrentSec = setCurrentSec
  }

  /** 开始 rAF 循环（若已在运行则先停止再重启）。 */
  start(): void {
    this.stop()
    this.hasFirstFrame = false
    this.lastTs = 0
    const step = (ts: number) => {
      if (!this.getIsPlaying()) {
        this.rafId = 0
        return
      }
      if (!this.hasFirstFrame) {
        this.hasFirstFrame = true
        this.lastTs = ts
        this.rafId = requestAnimationFrame(step)
        return
      }
      const duration = this.getDurationSec()
      let dt = (ts - this.lastTs) / 1000
      this.lastTs = ts
      if (!Number.isFinite(dt) || dt < 0) dt = 0
      if (dt > MAX_DT_SEC) dt = MAX_DT_SEC
      const cur = this.getCurrentSec()
      const next = cur + dt
      if (next >= duration) {
        this.setCurrentSec(Number(duration.toFixed(4)))
        this.stopPlaying()
        this.rafId = 0
        return
      }
      this.setCurrentSec(Number(next.toFixed(4)))
      this.rafId = requestAnimationFrame(step)
    }
    this.rafId = requestAnimationFrame(step)
  }

  /** 取消动画帧请求。 */
  stop(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = 0
    this.hasFirstFrame = false
  }
}
